import cronstrue from 'cronstrue';
import { BadgeVariant } from 'pounceV2';
import { ScheduledPromptRunStatus } from 'Generated/schema';

export const getRunStatusVariant = (
  status: ScheduledPromptRunStatus | null | undefined,
): BadgeVariant => {
  switch (status) {
    case ScheduledPromptRunStatus.Succeeded:
      return 'success-outline';
    case ScheduledPromptRunStatus.Failed:
      return 'error-outline';
    case ScheduledPromptRunStatus.Running:
      return 'info-outline';
    default:
      return 'neutral-outline';
  }
};

/**
 * Formats a schedule object into a human-readable string
 */
export const formatSchedule = (schedule: {
  rateMinutes?: number | null;
  cronExpression?: string | null;
}): string => {
  if (schedule.rateMinutes != null) {
    const hours = Math.floor(schedule.rateMinutes / 60);
    const mins = schedule.rateMinutes % 60;

    if (hours > 0 && mins > 0) {
      return `Every ${hours}h ${mins}m`;
    }

    if (hours > 0) {
      return `Every ${hours} hour${hours > 1 ? 's' : ''}`;
    }

    return `Every ${mins} minute${mins > 1 ? 's' : ''}`;
  }

  if (schedule.cronExpression) {
    try {
      return cronstrue.toString(schedule.cronExpression, {
        use24HourTimeFormat: true,
        verbose: true,
      });
    } catch (error) {
      // Fallback to raw cron if parsing fails
      return schedule.cronExpression;
    }
  }

  return 'Not configured';
};

/**
 * Transforms form values into a schedule object for API submission
 */
export const transformFormValuesToSchedule = (values: {
  scheduleType: 'rate' | 'cron';
  periodDays?: number;
  periodHours?: number;
  periodMinutes?: number;
  minutes?: string;
  hours?: string;
  days?: string;
  months?: string;
  weekDays?: string;
  enabled: boolean;
}): {
  rateMinutes?: number;
  cronExpression?: string;
  timeout: number;
  disabled: boolean;
} => {
  return values.scheduleType === 'rate'
    ? {
        rateMinutes:
          (values.periodDays || 0) * 1440 +
          (values.periodHours || 0) * 60 +
          (values.periodMinutes || 0),
        timeout: 10,
        disabled: !values.enabled,
      }
    : {
        cronExpression: `${values.minutes ?? '*'} ${values.hours ?? '*'} ${values.days ?? '*'} ${
          values.months ?? '*'
        } ${values.weekDays ?? '*'}`,
        timeout: 10,
        disabled: !values.enabled,
      };
};

/**
 * Parses a schedule object into form values for editing
 */
export const parseScheduleToFormValues = (schedule?: {
  rateMinutes?: number | null;
  cronExpression?: string | null;
  disabled?: boolean | null;
}): {
  scheduleType: 'rate' | 'cron';
  periodDays: number;
  periodHours: number;
  periodMinutes: number;
  minutes: string;
  hours: string;
  days: string;
  months: string;
  weekDays: string;
  enabled: boolean;
} => {
  const scheduleType = schedule?.cronExpression ? 'cron' : 'rate';

  // Parse rate minutes
  const rateMinutes = schedule?.rateMinutes ?? 0;
  const rateDays = Math.floor(rateMinutes / 1440);
  const rateHours = Math.floor((rateMinutes % 1440) / 60);
  const rateMinutesRemainder = rateMinutes % 60;

  // Parse cron expression
  let minutes = '*';
  let hours = '*';
  let days = '*';
  let months = '*';
  let weekDays = '*';

  if (schedule?.cronExpression) {
    const cronParts = schedule.cronExpression.split(' ');
    if (cronParts.length === 5) {
      [minutes, hours, days, months, weekDays] = cronParts;
    }
  }

  return {
    scheduleType,
    periodDays: rateDays,
    periodHours: rateHours,
    periodMinutes: rateMinutesRemainder,
    minutes,
    hours,
    days,
    months,
    weekDays,
    enabled: !schedule?.disabled,
  };
};
